import { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext.tsx';

// ─── BypassCountdown — top-bar badge for an active admin-granted access bypass (v143) ─────────
// Shown for the whole session whenever user.bypassExpiresAt is non-null -- that field is set by
// the server (server/src/lib/planTier.ts's resolveAccessLock, surfaced through /auth/me and
// /auth/login in server/src/routes/auth.ts) ONLY when current access comes from a bypass rather
// than a real Stripe subscription, so a subscribed org/facility never shows this. Ticks client-side
// off a plain setInterval; no server round-trip needed since the countdown is just arithmetic
// against a fixed timestamp already in hand. When it hits zero it keeps showing "Expired" rather
// than disappearing -- the actual re-lock still needs a fresh /auth/me (next natural request, e.g.
// AuthContext's own periodic activity, or the user re-navigating) since this badge has no way to
// force server re-evaluation early; that's an acceptable few-second-to-next-request gap for a
// countdown display, not a security boundary (the server enforces the real deadline on every
// request regardless of what this badge shows).
function formatRemaining(ms: number): string {
  if (ms <= 0) return 'Expired';
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export default function BypassCountdown() {
  const { user } = useAuth();
  const expiresAt = user?.bypassExpiresAt ?? null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (!expiresAt) return null;

  const remainingMs = new Date(expiresAt).getTime() - now;
  const expired = remainingMs <= 0;
  const tierLabel = user?.bypassTier ? `${user.bypassTier.charAt(0).toUpperCase()}${user.bypassTier.slice(1)} · ` : '';

  return (
    <span
      title={`Access granted by admin bypass, ${expired ? 'expired' : 'expires'} ${new Date(expiresAt).toLocaleString()}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 999,
        fontSize: '0.7rem', fontWeight: 500,
        background: expired ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
        color: expired ? '#ef4444' : '#f59e0b',
        border: `1px solid ${expired ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)'}`,
        whiteSpace: 'nowrap',
      }}
    >
      <Timer size={10} />
      {tierLabel}Bypass: {formatRemaining(remainingMs)}
    </span>
  );
}
